import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import type { ICategoriesApiArgs, TCategoriesApiResponse } from '../../models';
import { sentryLogError } from '../../utils';
import { api } from '../../api';

import type { IBasketSlice, TCreateThunkApi } from './models';

const initialState: IBasketSlice = {
    products: [],
    services: [],
    availableProducts: [],
    availableServices: [],
    subscription: undefined,
    errorProductsIds: undefined,
    selectedCategories: [],
    offerId: undefined,
    groupId: undefined,
};

export const basketSlice = createSlice({
    name: 'basket',
    initialState,
    reducers: {
        addProduct: (state, action: PayloadAction<string>) => {
            state.products.push(action.payload);
        },
        removeProduct: (state, action: PayloadAction<string>) => {
            const idx = state.products.findIndex(item => item === action.payload);

            if (idx > -1) {
                state.products.splice(idx, 1);
            }
        },
        addService: (state, action: PayloadAction<string>) => {
            state.services.push(action.payload);
        },
        removeService: (state, action: PayloadAction<string>) => {
            const idx = state.services.findIndex(item => item === action.payload);

            if (idx > -1) {
                state.services.splice(idx, 1);
            }
        },
        setProducts: (state, action: PayloadAction<string[]>) => {
            state.products = [...state.products, ...action.payload];
        },
        setServices: (state, action: PayloadAction<string[]>) => {
            state.services = [...state.services, ...action.payload];
        },
        setSubscription: (state, action: PayloadAction<string | undefined>) => {
            state.subscription = action.payload;
        },
        setAvailableAllProducts: (
            state,
            action: PayloadAction<Pick<IBasketSlice, 'availableProducts' | 'availableServices'>>
        ) => {
            const { availableProducts, availableServices } = action.payload;

            state.availableProducts = availableProducts;
            state.availableServices = availableServices;
        },
        setErrorProductsIds: (state, action: PayloadAction<string[]>) => {
            state.errorProductsIds = action.payload;
        },
        setAvailableCategories: (state, action: PayloadAction<TCategoriesApiResponse>) => {
            state.availableCategories = action.payload;
        },
        setSelectedCategories: (
            state,
            action: PayloadAction<{
                categories: IBasketSlice['selectedCategories'];
                groupId: number;
                offerId: number;
            }>
        ) => {
            const { categories, groupId, offerId } = action.payload;

            state.selectedCategories = categories;
            state.groupId = groupId;
            state.offerId = offerId;
        },
        resetBasket: () => ({ ...initialState }),
    },
});

export const {
    addProduct,
    addService,
    removeProduct,
    removeService,
    resetBasket,
    setAvailableAllProducts,
    setAvailableCategories,
    setErrorProductsIds,
    setProducts,
    setSelectedCategories,

    setServices,
    setSubscription,
} = basketSlice.actions;

/** Запрос на получание категорий кэшбэка */
export const getCategoriesRequest = createAsyncThunk<void, ICategoriesApiArgs, TCreateThunkApi>(
    'getCategoriesRequest',
    async (groupType, { dispatch }) => {
        try {
            const { data } = await api.getCashbackCategories(groupType);

            dispatch(setAvailableCategories(data));
        } catch (error) {
            sentryLogError({
                error,
                message: 'getCategoriesRequest error',
                data: groupType,
            });
        }
    }
);
